import React, { useEffect } from 'react';

import { Link, useHistory } from 'react-router-dom';

import {
  AimOutlined,
  AlertOutlined,
  DashboardOutlined,
  EnvironmentOutlined,
  ExceptionOutlined,
  RightOutlined,
  ShopOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { AsyncJobStepName } from '@recurrency/core-api-schema/dist/asyncJobs/common';
import {
  OrderDispositionFilterOptions,
  PurchaseTargetStatus,
  SpecialOrderFilterOptions,
  SupplierTargetTypes,
  TargetGroupType,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { PurchaseTargetDTO } from '@recurrency/core-api-schema/dist/purchasing/getPurchaseTargets';
import { Radio } from 'antd';
import { theme } from 'theme';

import { AsyncMultiSelect } from 'components/AsyncSelect/AsyncMultiSelect';
import { MultiSelectOption } from 'components/AsyncSelect/types';
import { convertToMultiSelectProps } from 'components/AsyncSelect/useAsyncMultiSelectProps';
import {
  useLocationsSelectProps,
  usePurchasingBuyersSelectProps,
  useSuppliersSelectProps,
} from 'components/AsyncSelect/useAsyncSelectProps';
import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { Button } from 'components/Button';
import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpace } from 'components/FlexSpace';
import { FlexSpacer } from 'components/FlexSpacer';
import { PageHeader } from 'components/PageHeader';
import { RadioGroup } from 'components/Radio/RadioGroup';
import { ColumnChooserSection } from 'components/recipes/ColumnChooserSection';
import { EditTargetActionButton } from 'components/recipes/EditTargetActionButton';
import { PriorityDispositionEquationPopover } from 'components/recipes/equation/PriorityDispositionEquationPopover';
import { BadgeStatus, StatusBadge } from 'components/recipes/StatusBadge';
import { ResultCount } from 'components/ResultCount';
import { SelectionCountAndClear } from 'components/SelectionCountAndClear';
import { Tooltip } from 'components/Tooltip';
import { IconTooltip } from 'components/Tooltip/IconTooltip';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useCoreApi } from 'hooks/useApi';
import { useGlobalApp } from 'hooks/useGlobalApp';

import { truthy } from 'utils/boolean';
import { formatNumber, formatTargetProgressPercent, formatUSD, pluralize } from 'utils/formatting';
import { objPickKeys } from 'utils/object';
import { hasUserPermission } from 'utils/roleAndTenant';
import { routes, useHashState } from 'utils/routes';
import { PersistedColumn, ViewSettingKey } from 'utils/tableAndSidePaneSettings/types';
import { useUserViewSettingsState } from 'utils/tableAndSidePaneSettings/useUserViewSettingsState';
import {
  asKeyOf,
  sortableDateColumn,
  sortableDollarColumn,
  sortableIdColumn,
  sortableNumberColumn,
  sortablePercentColumn,
  sortDirections,
  withSortedColumn,
} from 'utils/tables';

import { PurchaseTargetsHashState } from 'types/hash-state';

import { arrUnique } from '../../../utils/array';
import {
  editButtonOnClick,
  recordToSupplierLocationWTarget,
  specialOrderFilterOptions,
  TargetTypeLabels,
  TargetTypesFilterOptions,
} from '../utils';
import { usePurchasingStatuses } from './PurchaseTargetLinesPage/utils';
import { SortResponse } from './types';

const statusOptions: MultiSelectOption[] = [
  { label: 'All', value: '' },
  { label: 'Ready', value: PurchaseTargetStatus.Ready },
  { label: 'Not Ready', value: PurchaseTargetStatus.NotReady },
];

export function PurchaseTargetsPage() {
  const history = useHistory();
  const { activeTenant } = useGlobalApp();
  const canExportOrders = hasUserPermission(activeTenant, TenantUserPermission.ActionExportPurchaseTransferOrders);
  const [hashState, updateHashState] = useHashState<PurchaseTargetsHashState>();
  const {
    targetGroupType = TargetGroupType.Targets,
    targetTypes,
    selectedSupplierLocations = [],
    locationIds = [],
    supplierIds = [],
    buyerIds = [],
    specialOrder = [],
    orderDisposition = [],
    includeInactive = true,
  } = hashState;
  const locationsSelectProps = useLocationsSelectProps({});
  const suppliersSelectProps = useSuppliersSelectProps();
  const buyerSelectProps = usePurchasingBuyersSelectProps();
  const { shouldShowSpecialOrderWarning, enabledPriorityDisposition, automaticallyRecommendPurchasingSpecials } =
    usePurchasingStatuses();
  const initialSortState = getTargetGroupSort(targetGroupType);
  const tableProps = useCoreApiTableProps({
    schema: schemas.purchasing.getPurchaseTargets,
    queryParams: {
      targetGroupType,
      filter: {
        locationIds,
        supplierIds,
        buyerIds,
        status: hashState.status,
        targetTypes,
        specialOrder,
        orderDisposition,
        includeInactive: hashState.includeInactive ?? true,
      },
    },
    initialState: initialSortState,
  });
  const { items: targetsData, reload: reloadPurchaseTargets, setPage, setSortBy, setSortDir } = tableProps;
  const viewSettingKey =
    targetGroupType === TargetGroupType.Targets
      ? ViewSettingKey.PurchaseTargetsTable
      : ViewSettingKey.PurchaseTargetsReviewCycleTable;

  const { data: inProgressTransformSyncs } = useCoreApi(schemas.asyncJobs.getAsyncJobSteps, {
    queryParams: {
      filter: {
        stepNames: [AsyncJobStepName.SyncSearchIndex, AsyncJobStepName.SyncSearchIndexMerged],
        currentUser: true,
      },
    },
  });

  /** the purpose of this string is to memoize these values so that the useEffect below doesn't trigger when the values are the same.
   * The arrays in hashState are recreated per render and considered new objects, causing re-renders if added directly to the useEffect dependency array */
  const stringifyHashState = JSON.stringify(
    objPickKeys(
      hashState,
      'status',
      'targetGroupType',
      'supplierIds',
      'locationIds',
      'buyerIds',
      'targetTypes',
      'specialOrder',
      'orderDisposition',
      'includeInactive',
    ),
  );

  useEffect(() => {
    setPage(1);
  }, [stringifyHashState, setPage]);

  const handleTargetValueChange = () => {
    reloadPurchaseTargets();
  };

  const setTargetGroupTypeFilter = (newTargetGroupType: TargetGroupType | undefined) => {
    updateHashState({ targetGroupType: newTargetGroupType });
    const newSort = getTargetGroupSort(newTargetGroupType);
    setSortBy?.(newSort.sortBy);
    setSortDir?.(newSort.sortDir);
    updateHashState({ targetTypes: undefined });
  };

  const setIncludeInactiveFilter = (includeInactive: boolean) => {
    updateHashState({ includeInactive });
  };

  const tableColumns: PersistedColumn<PurchaseTargetDTO>[] = [
    sortableIdColumn({
      title: 'Supplier',
      dataIndex: asKeyOf<PurchaseTargetDTO>('supplierId'),
      sorter: true,
      settingKey: 'supplierId',
      required: true,
      render: (_, record: PurchaseTargetDTO) => `${record.supplierId}: ${record.supplierName}`,
    }),
    sortableIdColumn({
      title: 'Location',
      dataIndex: asKeyOf<PurchaseTargetDTO>('purchaseLocationId'),
      sorter: true,
      settingKey: 'purchaseLocationId',
      required: true,
      render: (_, record: PurchaseTargetDTO) => `${record.purchaseLocationId}: ${record.purchaseLocationName}`,
    }),
    sortableDateColumn({
      title: 'Last Ordered',
      dataIndex: asKeyOf<PurchaseTargetDTO>('lastOrderDate'),
      align: 'right',
      sorter: true,
      settingKey: 'lastOrderDate',
    }),
    ...(targetGroupType === TargetGroupType.ReviewCycle
      ? [
          sortableDateColumn({
            title: 'Last Reviewed',
            dataIndex: asKeyOf<PurchaseTargetDTO>('lastReviewDate'),
            align: 'right' as const,
            sorter: true,
            settingKey: 'lastReviewDate',
          }),
          sortableNumberColumn({
            title: 'Review Cycle (Days)',
            dataIndex: asKeyOf<PurchaseTargetDTO>('reviewCycleDays'),
            align: 'right' as const,
            sorter: true,
            settingKey: 'reviewCycleDays',
            render: (reviewCycleDays: number, record: PurchaseTargetDTO) => (
              <EditTargetActionButton
                value={reviewCycleDays}
                onClick={() => editButtonOnClick(record.supplierId, record.purchaseLocationId, handleTargetValueChange)}
              />
            ),
          }),
          sortableDateColumn({
            title: 'Next Review',
            dataIndex: asKeyOf<PurchaseTargetDTO>('nextReviewDate'),
            align: 'right' as const,
            sorter: true,
            settingKey: 'nextReviewDate',
          }),
        ]
      : [
          {
            title: 'Current',
            dataIndex: asKeyOf<PurchaseTargetDTO>('currentValue'),
            sorter: true,
            sortDirections,
            align: 'right' as const,
            settingKey: 'currentValue',
            render: (currentValue: number, record: PurchaseTargetDTO) =>
              record.targetType === 'dollars' ? formatUSD(currentValue) : formatNumber(currentValue, 0),
          },
          {
            title: 'Target',
            dataIndex: asKeyOf<PurchaseTargetDTO>('targetValue'),
            sorter: true,
            sortDirections,
            align: 'right' as const,
            settingKey: 'targetValue',
            render: (targetValue: number, record: PurchaseTargetDTO) => (
              <EditTargetActionButton
                value={record.targetType === 'dollars' ? formatUSD(targetValue) : formatNumber(targetValue)}
                onClick={() => editButtonOnClick(record.supplierId, record.purchaseLocationId, handleTargetValueChange)}
              />
            ),
          },
          {
            title: 'Target Type',
            dataIndex: asKeyOf<PurchaseTargetDTO>('targetType'),
            align: 'right' as const,
            settingKey: 'targetType',
            render: (targetTypeVal: SupplierTargetTypes | null) =>
              targetTypeVal ? TargetTypeLabels[targetTypeVal] : '-',
          },
          sortablePercentColumn({
            title: 'Target %',
            dataIndex: asKeyOf<PurchaseTargetDTO>('goalPercent'),
            settingKey: 'goalPercent',
            render: (val: number) => formatTargetProgressPercent(val),
            sorter: true,
          }),
        ]),
    {
      title: 'Status',
      dataIndex: asKeyOf<PurchaseTargetDTO>('status'),
      settingKey: 'status',
      render: (status: BadgeStatus) => <StatusBadge status={status} />,
    },
    sortableDollarColumn({
      title: 'PO Cost',
      dataIndex: asKeyOf<PurchaseTargetDTO>('orderCost'),
      sorter: true,
      settingKey: 'orderCost',
      render: (orderCost, record) =>
        formatUSD(orderCost + (automaticallyRecommendPurchasingSpecials ? record.specialOrderCost : 0)),
    }),
    sortableNumberColumn({
      title: 'Lines',
      dataIndex: asKeyOf<PurchaseTargetDTO>('numLines'),
      sorter: true,
      settingKey: 'numLines',
      render: (_, row) => (
        <>
          {shouldShowSpecialOrderWarning && (row.numLinesOnSpecialOrder ?? 0) > 0 && (
            <IconTooltip
              tooltipText="There is a special order available to be combined with this stock PO"
              wrapText
              Icon={ExceptionOutlined}
              iconStyle={{ color: theme.colors.twilight[700] }}
            />
          )}
          {row.numLines}
        </>
      ),
    }),
    canExportOrders && {
      title: 'Actions',
      width: '100px',
      align: 'left' as const,
      settingKey: 'actions',
      render: (_: unknown, record: PurchaseTargetDTO) => (
        <Button
          size="small"
          data-test-id="detailPageButton"
          disabled={selectedSupplierLocations.length > 0}
          onClick={() => {
            history.push(
              routes.purchasing.purchaseTargetLines({
                companyIds: record.companyId ? [record.companyId] : undefined,
                supplierLocations: [recordToSupplierLocationWTarget(record)],
              }),
            );
          }}
        >
          Lines <RightOutlined />
        </Button>
      ),
    },
    // optional columns
    {
      title: 'Supplier Minimum',
      dataIndex: asKeyOf<PurchaseTargetDTO>('minimumTargetValue'),
      settingKey: 'minimumTargetValue',
      render: (minimumTargetValue: number, record: PurchaseTargetDTO) => (
        <EditTargetActionButton
          value={
            record.minimumTargetType === SupplierTargetTypes.Dollars
              ? formatUSD(minimumTargetValue)
              : formatNumber(minimumTargetValue)
          }
          onClick={() => editButtonOnClick(record.supplierId, record.purchaseLocationId, handleTargetValueChange)}
        />
      ),
      optional: true,
    },
    {
      title: 'Supplier Minimum Type',
      dataIndex: asKeyOf<PurchaseTargetDTO>('minimumTargetType'),
      settingKey: 'minimumTargetType',
      render: (minimumTargetType: SupplierTargetTypes) =>
        minimumTargetType ? TargetTypeLabels[minimumTargetType] : '-',
      optional: true,
    },

    sortableDollarColumn({
      title: (
        <InfoTooltip
          title={`Cost of backordered ${shouldShowSpecialOrderWarning ? 'and special order ' : ''}lines`}
          useInfoIcon
        >
          Priority Cost
        </InfoTooltip>
      ),
      dataIndex: asKeyOf<PurchaseTargetDTO>('priorityDispositionCost'),
      settingKey: 'priorityDispositionCost',
      render: (priorityDispositionCost: number, record: PurchaseTargetDTO) => (
        <>
          {formatUSD(priorityDispositionCost)}
          <PriorityDispositionEquationPopover
            type="Cost"
            total={priorityDispositionCost}
            specialOrderQty={record.specialOrderCost}
            backorderQty={record.backorderedCost}
          />
        </>
      ),
      optional: true,
      sorter: true,
    }),
    sortableNumberColumn({
      title: 'Priority Lines',
      dataIndex: asKeyOf<PurchaseTargetDTO>('numLinesPriorityDisposition'),
      settingKey: 'numLinesPriorityDisposition',
      render: (priorityLines: number, record: PurchaseTargetDTO) => (
        <>
          {formatNumber(priorityLines)}
          <PriorityDispositionEquationPopover
            total={priorityLines}
            specialOrderQty={record.numLinesOnSpecialOrder}
            backorderQty={record.numLinesOnBackorder}
            type="Lines"
          />
        </>
      ),
      optional: true,
      sorter: true,
    }),
  ].filter(truthy);

  const [visibleColumnKeys, setVisibleColumnKeys] = useUserViewSettingsState(
    viewSettingKey,
    tableColumns.filter((column) => !column.optional),
  );

  return (
    <Container>
      <PageHeader
        title={
          <InfoTooltip
            title={[
              'Recurrency tracks all your items and notifies you when items ready-to-purchase surpass a supplier purchase target.',
              'Use this page to configure your targets and review suppliers ready for a PO.',
              "Select items and then click 'PO Builder' for building multiple POs simultaneously.",
            ].join(' ')}
          >
            Purchase Targets
          </InfoTooltip>
        }
        subtitle={
          inProgressTransformSyncs && inProgressTransformSyncs.totalCount > 0 ? (
            <span>
              Records will update when your{' '}
              <Link to={routes.monitoring.exportJobs()}>
                {pluralize(inProgressTransformSyncs.totalCount, 'recent export')}
              </Link>{' '}
              finishes processing.
            </span>
          ) : null
        }
        headerActions={
          <Button
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionExportPurchaseTransferOrders]}
            disabled={selectedSupplierLocations.length === 0}
            onClick={() => {
              history.push(
                routes.purchasing.purchaseTargetLines({
                  companyIds: arrUnique((targetsData || []).map((t) => t.companyId)).filter((c) => !!c),
                  supplierLocations: selectedSupplierLocations,
                }),
              );
            }}
          >
            PO Builder
          </Button>
        }
      />
      <FilterBarBox dividerLine>
        <FlexSpace direction="column" gap={16} fullWidth>
          <FlexSpace>
            <span>View</span>
            <RadioGroup
              value={targetGroupType}
              onChange={(ev) => {
                const newTargetGroupType = ev.target.value as TargetGroupType;
                setTargetGroupTypeFilter(newTargetGroupType);
              }}
            >
              <Radio.Button value={TargetGroupType.Targets}>Targets</Radio.Button>
              <Radio.Button value={TargetGroupType.ReviewCycle}>Review Cycle</Radio.Button>
            </RadioGroup>
            <RadioGroup
              value={includeInactive ? 'includeInactive' : 'activeOnly'}
              onChange={(ev) => {
                const newIncludeInactive = ev.target.value === 'includeInactive';
                setIncludeInactiveFilter(newIncludeInactive);
              }}
            >
              <Radio.Button value="activeOnly">
                <Tooltip title="Show only active purchase targets where there are target lines.">Hide Inactive</Tooltip>
              </Radio.Button>
              <Radio.Button value="includeInactive">Show All</Radio.Button>
            </RadioGroup>
          </FlexSpace>

          <FlexSpace wrap fullWidth>
            <span>Filter</span>
            <AsyncMultiSelect
              label="Status"
              mode="single"
              icon={<DashboardOutlined />}
              selectProps={{ options: statusOptions }}
              selectedValues={[hashState.status || statusOptions[0].value]}
              onSelectedValuesChange={(values) =>
                updateHashState({ status: (values[0] || undefined) as PurchaseTargetStatus })
              }
            />
            {targetGroupType === TargetGroupType.Targets ? (
              <AsyncMultiSelect
                label="Target Type"
                icon={<AimOutlined />}
                selectProps={{ options: TargetTypesFilterOptions }}
                selectedValues={targetTypes || []}
                onSelectedValuesChange={(values) =>
                  updateHashState({ targetTypes: values.length > 0 ? (values as SupplierTargetTypes[]) : undefined })
                }
              />
            ) : null}
            <AsyncMultiSelect
              selectProps={convertToMultiSelectProps(suppliersSelectProps)}
              label="Supplier"
              queryPlaceholder="Search suppliers"
              selectedValues={supplierIds}
              onSelectedValuesChange={(values) => updateHashState({ supplierIds: values })}
              icon={<ShopOutlined />}
            />
            <AsyncMultiSelect
              selectProps={convertToMultiSelectProps(locationsSelectProps)}
              label="Location"
              queryPlaceholder="Search locations"
              selectedValues={locationIds}
              onSelectedValuesChange={(values) => updateHashState({ locationIds: values })}
              icon={<EnvironmentOutlined />}
            />
            <AsyncMultiSelect
              selectProps={convertToMultiSelectProps(buyerSelectProps)}
              label="Buyer"
              queryPlaceholder="Search buyer"
              selectedValues={buyerIds}
              onSelectedValuesChange={(values) => updateHashState({ buyerIds: values })}
              icon={<UserOutlined />}
            />
            {shouldShowSpecialOrderWarning && (
              <AsyncMultiSelect
                selectProps={{ options: specialOrderFilterOptions }}
                label="Special Orders"
                selectedValues={specialOrder}
                onSelectedValuesChange={(values) =>
                  updateHashState({
                    specialOrder: values.length > 0 ? (values as SpecialOrderFilterOptions[]) : undefined,
                  })
                }
                icon={<ExceptionOutlined />}
              />
            )}
            <AsyncMultiSelect
              label="Priority"
              disableValuesTooltip
              selectProps={{ options: enabledPriorityDisposition }}
              selectedValues={orderDisposition}
              onSelectedValuesChange={(values) =>
                updateHashState({
                  orderDisposition: values.length > 0 ? (values as OrderDispositionFilterOptions[]) : undefined,
                })
              }
              icon={<AlertOutlined />}
            />
            {hashState.status ||
            hashState.supplierIds ||
            hashState.locationIds ||
            hashState.targetTypes ||
            (hashState.specialOrder && hashState.specialOrder.length > 0) ||
            hashState.orderDisposition ||
            hashState.buyerIds ? (
              <ActionButton
                onClick={() =>
                  updateHashState({
                    status: undefined,
                    supplierIds: undefined,
                    locationIds: undefined,
                    buyerIds: undefined,
                    targetTypes: undefined,
                    specialOrder: undefined,
                    orderDisposition: undefined,
                  })
                }
                label="Clear All"
              />
            ) : null}
            <FlexSpacer />
            <ResultCount count={tableProps.totalCount} dataTestId="purchase-targets-result-count" />
          </FlexSpace>
        </FlexSpace>
      </FilterBarBox>

      <SelectionCountAndClear
        numSelected={selectedSupplierLocations.length}
        entitySingle="row"
        onClear={() => updateHashState({ selectedSupplierLocations: undefined })}
      />

      <ColumnChooserSection
        tableKey={viewSettingKey}
        columns={tableColumns}
        visibleColumnKeys={visibleColumnKeys}
        setVisibleColumnKeys={setVisibleColumnKeys}
      />

      <AsyncTable
        stickyHeader
        tableProps={tableProps}
        columns={withSortedColumn(
          visibleColumnKeys.map((key) => tableColumns.find((column) => column.settingKey === key)).filter(truthy),
          tableProps.sortBy,
          tableProps.sortDir,
        )}
        rowKey={({ supplierId, purchaseLocationId }) => getRowKey({ supplierId, locationId: purchaseLocationId })}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedSupplierLocations.map(getRowKey),
          onChange: (newSelectedRowKeys: React.Key[]) => {
            const newSelection = newSelectedRowKeys.map(
              (key) =>
                selectedSupplierLocations.find((sl) => getRowKey(sl) === key) ||
                recordToSupplierLocationWTarget(
                  targetsData.find(
                    ({ supplierId, purchaseLocationId }) =>
                      getRowKey({ supplierId, locationId: purchaseLocationId }) === key,
                  )!,
                ),
            );
            updateHashState({ selectedSupplierLocations: newSelection });
          },
        }}
      />
    </Container>
  );
}

const getRowKey = (record: { supplierId: string; locationId: string }) => `${record.supplierId}|${record.locationId}`;

const getTargetGroupSort = (targetGroupType?: TargetGroupType): SortResponse => {
  if (targetGroupType === TargetGroupType.ReviewCycle) {
    return { sortBy: 'orderCost', sortDir: 'desc' };
  }
  return { sortBy: 'goalPercent', sortDir: 'desc' };
};
